import React from "react";
import { Butterfly, Worm, WalkingChild, Flower, TreeActor, SunActor, Bird, Cloud } from "../actors";

/*
 * Composed STORY scenes — built by choreographing the SHARED reusable actors
 * (actors.tsx) along paths, instead of hand-drawing everything inline. This is
 * the v2 direction: one Butterfly actor flies in the life-cycle lesson AND leads
 * a child down a road here — same component, same animation, reused. Scenes are
 * arrangements + motion paths over the actor library.
 */

export interface StorySceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const JourneyScene: React.FC<StorySceneProps> = ({
  opacity = 1, accentColor = "#8b5cf6", t = 0,
  label = "Every story is a journey — let's follow along!",
}) => {
  void accentColor;
  const roadY = 360;
  const ws = 90; // world scroll speed (the world slides past → forward travel)
  // Wrap a base x so scenery scrolls left forever and re-enters from the right.
  const wrapX = (base: number, period: number) => (((base - (t * ws) % period) + period) % period) - 120;

  const childX = 250, childY = roadY + 6;
  // The butterfly leads just ahead of the child along a gentle weaving path.
  const bfX = 410 + Math.sin(t * 0.8) * 45;
  const bfY = 220 + Math.sin(t * 1.6) * 26;
  const dash = (t * ws) % 60;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="js-sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#cfeafe" /><stop offset="100%" stopColor="#eaf7ff" /></linearGradient>
      </defs>
      <rect x={0} y={0} width={800} height={roadY + 16} fill="url(#js-sky)" />
      <rect x={0} y={roadY + 16} width={800} height={450 - (roadY + 16)} fill="#86efac" />
      <ellipse cx={170} cy={roadY + 30} rx={240} ry={70} fill="#4ade80" opacity={0.6} />
      <ellipse cx={640} cy={roadY + 30} rx={260} ry={80} fill="#4ade80" opacity={0.5} />

      {SunActor(690, 78, t, 1)}

      {/* drifting clouds + birds (reused actors) */}
      {[0, 1, 2].map((i) => Cloud(wrapX(120 + i * 300, 980), 70 + (i % 2) * 36, 0.8 + (i % 2) * 0.3))}
      {[0, 1, 2].map((i) => <g key={`b${i}`}>{Bird(wrapX(200 + i * 260, 900), 120 + (i % 3) * 22, t, 1)}</g>)}

      {/* Road */}
      <rect x={0} y={roadY + 16} width={800} height={50} fill="#334155" />
      {Array.from({ length: 16 }, (_, i) => (
        <rect key={i} x={i * 60 - dash} y={roadY + 38} width={32} height={5} rx={2.5} fill="#fde047" opacity={0.9} />
      ))}

      {/* Scrolling roadside trees + flowers (reused actors, wrapping) */}
      {[0, 1, 2, 3, 4].map((i) => <g key={`t${i}`}>{TreeActor(wrapX(i * 210, 1050), roadY + 16, t, 0.85)}</g>)}
      {[0, 1, 2, 3, 4, 5].map((i) => <g key={`f${i}`}>{Flower(wrapX(60 + i * 150, 900), roadY + 60, t, 0.7, i % 2 ? "#fb7185" : "#a78bfa")}</g>)}

      {/* A worm crawling on the verge (reused actor) */}
      {Worm(wrapX(120, 700), roadY + 64, t, 0.8)}

      {/* The child follows the butterfly down the road */}
      {WalkingChild(childX, childY, t, 1.15)}
      {Butterfly(bfX, bfY, t, 1.1, "#f59e0b")}
      {/* a dotted "following" trail from child's eyes toward the butterfly */}
      <path d={`M${childX + 14},${childY - 38} Q${(childX + bfX) / 2},${bfY - 10} ${bfX},${bfY + 14}`} fill="none" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="2 7" opacity={0.5} />

      {/* Caption */}
      <g transform="translate(400 36)">
        <rect x={-255} y={-22} width={510} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
